import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Contact, CreateContactRequest, UpdateContactRequest } from '../../types/Contact';
import {
    CreateContactFieldRequest,
    DeleteContactFieldRequest,
    UpdateContactFieldRequest,
} from '../../types/ContactField';
import {
    CreateContactOwnerRequest,
    DeleteContactOwnerRequest,
    SwapContactOwnerRequest,
} from '../../types/ContactOwner';
import { CreateContactStatusRequest } from '../../types/ContactStatus';
import { CreateContactTagRequest } from '../../types/ContactTag';
import { requestContactById, requestCreateContact, requestDeleteContact } from '../contact/contact';
import {
    requestBatchUpdateContactFields,
    requestCreateContactField,
    requestDeleteContactField,
    requestUpdateContact,
    requestUpdateContactField,
} from '../contact/contactFields';
import {
    requestCreateContactOwner,
    requestDeleteContactOwnerFromFollowers,
    requestSwapContactOwner,
} from '../contact/contactOwners';
import { requestSearchQuickPhone } from '../contact/contactSearch';
import { requestAssignContactStatus, requestCreateContactStatus } from '../contact/contactStatuses';
import {
    requestAssignContactTag,
    requestCreateContactTag,
    requestDeleteContactTag,
    requestUnassignContactTag,
} from '../contact/contactTags';

export function useContact(contactId: number | undefined) {
    return useQuery({
        queryKey: [`contact-${contactId}`, 'data'],
        enabled: !!contactId,
        queryFn: async ({ signal }) => (await requestContactById(contactId!, signal))?.data,
    });
}

export function useDeleteContact(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => (await requestDeleteContact(contactId)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`] });
            queryClient.invalidateQueries({ queryKey: ['newAndRecent'] });
        },
    });
}

export function useSwapContactOwner(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: SwapContactOwnerRequest) =>
            (await requestSwapContactOwner(contactId, payload)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useCreateContactOwner(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: CreateContactOwnerRequest) =>
            (await requestCreateContactOwner(payload)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useDeleteContactOwnerFromFollowers(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: DeleteContactOwnerRequest) =>
            (await requestDeleteContactOwnerFromFollowers(payload, contactId)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useCreateContactField(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (field: CreateContactFieldRequest) =>
            (await requestCreateContactField(contactId, field)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useUpdateContactField(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (field: UpdateContactFieldRequest) =>
            (await requestUpdateContactField(contactId, field)).data,
        onMutate: async (field: UpdateContactFieldRequest) => {
            await queryClient.cancelQueries({ queryKey: [`contact-${contactId}`, 'data'] });
            const previousContact = queryClient.getQueryData([`contact-${contactId}`, 'data']);
            queryClient.setQueryData(
                [`contact-${contactId}`, 'data'],
                (old: Contact | undefined) => {
                    if (!old) return;
                    return {
                        ...old,
                        values: old?.values.map(value => {
                            if (value.fieldId === field.fieldId) {
                                value.value = field.value;
                                value.isVerified = field.isVerified;
                                return value;
                            }
                            return value;
                        }),
                    };
                },
            );
            return { previousContact };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useModifyContactField(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (field: UpdateContactRequest) =>
            (await requestUpdateContact(contactId, field)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useDeleteContactField(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (field: DeleteContactFieldRequest) =>
            (await requestDeleteContactField(contactId, field)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useBatchUpdateContactFields(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (fields: UpdateContactRequest[]) =>
            (await requestBatchUpdateContactFields(contactId, fields)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useAssignContactTag(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (tagId: number) =>
            (await requestAssignContactTag(tagId, [contactId])).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useCreateContactTag(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: CreateContactTagRequest) =>
            (await requestCreateContactTag(data)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
            queryClient.invalidateQueries({ queryKey: ['userTags'] });
        },
    });
}

export function useDeleteContactTag(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (tagId: number) => (await requestDeleteContactTag(tagId)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useUnassignContactTag(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (tagId: number) =>
            (await requestUnassignContactTag(tagId, [contactId])).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useAssignContactStatus(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (statusId: number) =>
            (await requestAssignContactStatus(statusId, [contactId])).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
        onMutate: async (statusId: number) => {
            await queryClient.cancelQueries({ queryKey: [`contact-${contactId}`, 'data'] });
            const previousContact = queryClient.getQueryData([`contact-${contactId}`, 'data']);
            queryClient.setQueryData(
                [`contact-${contactId}`, 'data'],
                (old: Contact | undefined) => {
                    if (!old) return;
                    return {
                        ...old,
                        statusId,
                    };
                },
            );
            return { previousContact };
        },
    });
}

export function useCreateContactStatus(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: CreateContactStatusRequest) =>
            (await requestCreateContactStatus(data)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`contact-${contactId}`, 'data'] });
        },
    });
}

export function useCreateContact() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: CreateContactRequest) => (await requestCreateContact(data)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['newAndRecent'] });
        },
    });
}

export function useSearchQuickPhone(search: string) {
    return useQuery({
        queryKey: [`searchQuickPhone`, search],
        queryFn: async ({ signal }) => (await requestSearchQuickPhone(search, signal))?.data,
    });
}
